import {
  IPriceListPanelProps,
  IListPrices,
  ICommonListProps,
} from '../../areas/product/components/price-list/price-list-panel/types';
import { OptionTypeEnum } from 'src/components/filter-dropdown';
import { FilterType, IFilter } from 'src/components/price-list/types';
import { AlternativePrice, Price } from 'src/types';
import { replaceMainOfferWithAlternativeOffer } from './utils';

const placeAfter = 1;
const placeBefore = -1;

type SortablePrice = Price | AlternativePrice;

export const getCommonListProps = (
  props: IPriceListPanelProps
): ICommonListProps => {
  return {
    productName: props.productName,
    media: props.media,
    brand: props.brand,
    campaign: props.campaign,
  };
};

const includeShipping = (filters: IFilter[]) =>
  filters.find(filter => filter.type === FilterType.PRICES_INC_SHIPPING);

const getTotalPrice = (offer: SortablePrice, filters: IFilter[]) => {
  let total = offer.price?.exclShipping || 0;
  if (includeShipping(filters)) {
    const shippingCost = offer.shipping?.fastest?.shippingCost || 0;
    total += shippingCost;
  }

  return total;
};

const sortDownloadable = (a: SortablePrice, b: SortablePrice): number => {
  if (a.type === 'download' && b.type === 'download') return 0;
  if (a.type === 'download' && b.type !== 'download') return placeBefore;
  if (a.type !== 'download' && b.type === 'download') return placeAfter;
  return 0;
};

const sortFeatured = (a: SortablePrice, b: SortablePrice): number => {
  if (!('store' in a) || !('store' in b)) return 0;
  if (a.store?.featured && b.store?.featured) return 0;
  if (a.store?.featured && !b.store?.featured) return placeBefore;
  if (!a.store?.featured && b.store?.featured) return placeAfter;
  return 0;
};

const sortStockStatus = (
  a: SortablePrice,
  b: SortablePrice,
  filters: IFilter[]
): number => {
  const statusesToCheck = ['unknown', 'not_in_stock', 'incoming'];
  const aStockStatusFail = statusesToCheck.includes(a.stock?.status || '');
  const bStockStatusFail = statusesToCheck.includes(b.stock?.status || '');

  if (aStockStatusFail || bStockStatusFail) {
    if (aStockStatusFail && b.stock?.status === 'unknown') {
      const aTotal = getTotalPrice(a, filters || []);
      const bTotal = getTotalPrice(b, filters || []);

      if (a.stock?.status === 'unknown' && aTotal > bTotal) {
        return placeAfter;
      }

      return placeBefore;
    }

    const isIncoming = a.stock?.status === 'incoming';
    if ((isIncoming && bStockStatusFail) || !aStockStatusFail) {
      return placeBefore;
    }

    return placeAfter;
  }

  return 0;
};

const sortShipping = (a: SortablePrice, b: SortablePrice): number => {
  const s1 = a.shipping?.fastest?.deliveryDays ?? null;
  const s2 = b.shipping?.fastest?.deliveryDays ?? null;
  if (s1 === null && s2 !== null) return placeAfter;
  if (s1 !== null && s2 === null) return placeBefore;
  if (s1 === null && s2 === null) return 0;
  if (s1!.min !== s2!.min) {
    return s1!.min - s2!.min;
  }
  return s1!.max - s2!.max;
};

const sortByFastest = (
  offerList: SortablePrice[],
  filters: IFilter[],
  isAlternative: Boolean = false
): SortablePrice[] => {
  if (offerList.length <= 0) {
    return offerList;
  }

  const sortedOffersAndAlternatives = [...offerList];

  sortedOffersAndAlternatives.sort((a, b) => {
    const downloadableSort = sortDownloadable(a, b);
    if (downloadableSort) {
      return downloadableSort;
    }

    const featuredSort = sortFeatured(a, b);
    if (featuredSort && !isAlternative) {
      return featuredSort;
    }

    const stockStatusSort = sortStockStatus(a, b, filters);
    if (stockStatusSort) {
      return stockStatusSort;
    }

    const shippingSort = sortShipping(a, b);
    if (shippingSort) {
      return shippingSort;
    }

    return 0;
  });

  return sortedOffersAndAlternatives;
};

const sortAlternativePricesByFastestDelivery = (
  offer: Price,
  filters: IFilter[]
) => {
  const altOffers = offer.alternativePrices ? [...offer.alternativePrices] : [];

  if (!altOffers.length) return offer.alternativePrices;

  return sortByFastest(
    altOffers as AlternativePrice[],
    filters,
    true
  ) as AlternativePrice[];
};

export const sortByFastestDelivery = (
  offerList: IListPrices,
  currentSorting: OptionTypeEnum,
  filters: IFilter[] = []
) => {
  const newOffers = { ...offerList };
  if (currentSorting === OptionTypeEnum.FASTEST_DELIVERY) {
    newOffers.mainList = sortByFastest(
      [...newOffers.mainList],
      filters
    ) as Price[];

    for (const offer of newOffers.mainList) {
      offer.alternativePrices = sortAlternativePricesByFastestDelivery(
        offer,
        filters
      );
    }

    newOffers.mainList = newOffers.mainList.map(offer =>
      replaceMainOfferWithAlternativeOffer(
        offer,
        offer.alternativePrices?.[0] as AlternativePrice
      )
    );
  }
  return newOffers;
};
